// Considered: MessageAtom, AlertCardMolecule and FilterConflictNoticeMolecule — the three
// things in this library that report a problem. All rejected for the same reason: each states
// ONE problem (severity + text, plus actions on AlertCardMolecule), while conformance findings
// are a LIST grouped by level, each row carrying a code chip, a severity and a clickable node
// path — a different IA, not a variant of a notice.
// See specs/space-conformance.md § Design System Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { ConformanceFinding, ConformanceLevel } from '@center-platform/space-format/conformance';
import './ConformanceFindingsListMolecule.scss';

export interface ConformanceFindingsListMoleculeProps {
  findings: ConformanceFinding[];
  /** Invoked with the resolved component id when a finding's path is clicked — wires into the composer's existing inspect hook. */
  onSelectNode?: (componentId: string) => void;
  className?: string;
  'data-testid'?: string;
}

/** Findings render grouped in level order, not insertion order — L0 structural errors are more actionable than L7 differential warnings. */
const LEVEL_ORDER: ConformanceLevel[] = ['L0', 'L1', 'L2', 'L3', 'L5', 'L7'];

/** `path` is `a2ui/components/<id>/<prop...>` — extracts `<id>` for the inspect hook, or undefined for paths that don't name a component (e.g. document-root findings). */
function extractComponentIdFromPath(path?: string): string | undefined {
  if (!path) return undefined;
  const match = /^a2ui\/components\/([^/]+)/.exec(path);
  return match ? match[1] : undefined;
}

const ConformanceFindingsListMolecule: React.FC<ConformanceFindingsListMoleculeProps> = ({
  findings,
  onSelectNode,
  className = '',
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  if (findings.length === 0) {
    return (
      <div className={`conformance-findings-list conformance-findings-list--empty ${className}`.trim()} data-testid={testId}>
        {t('conformance.findings.empty', { defaultValue: 'No conformance findings.' })}
      </div>
    );
  }

  const groups = LEVEL_ORDER
    .map((level) => ({ level, items: findings.filter((finding) => finding.level === level) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className={`conformance-findings-list ${className}`.trim()} data-testid={testId}>
      {groups.map((group) => (
        <div className="conformance-findings-list__group" key={group.level} data-testid={`conformance-findings-group-${group.level}`}>
          <div className="conformance-findings-list__group-heading">
            {t('conformance.findings.levelHeading', { level: group.level, defaultValue: '{{level}} findings' })}
          </div>
          {group.items.map((finding, index) => {
            const componentId = extractComponentIdFromPath(finding.path);
            // Two-tier message: the localized code sentence is the user-facing string; the checker's own
            // `message` is engine-generated diagnostic detail (naming the offending prop/ref/type) that no
            // locale bundle can carry, so it renders beneath — but not when it WAS the fallback.
            const localizedMessage = t(`conformance.findings.code.${finding.code}`, { defaultValue: finding.message });
            return (
              <div
                className={`conformance-findings-list__item conformance-findings-list__item--${finding.severity}`}
                key={`${finding.level}:${finding.code}:${finding.path ?? index}`}
              >
                <i
                  className={finding.severity === 'error' ? 'pi pi-times-circle' : 'pi pi-exclamation-triangle'}
                  aria-hidden="true"
                />
                <ChipAtom label={finding.code} title={localizedMessage} />
                <span className="conformance-findings-list__message" data-testid="conformance-finding-message">
                  {localizedMessage}
                  {localizedMessage !== finding.message && (
                    <span className="conformance-findings-list__detail" data-testid="conformance-finding-detail">
                      {finding.message}
                    </span>
                  )}
                </span>
                {componentId && onSelectNode && (
                  <ButtonAtom
                    variant="text"
                    size="small"
                    onClick={() => onSelectNode(componentId)}
                  >
                    {t('conformance.findings.viewNode', { defaultValue: 'View' })}
                  </ButtonAtom>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

ConformanceFindingsListMolecule.displayName = 'ConformanceFindingsListMolecule';

export default ConformanceFindingsListMolecule;
